import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { usersApi, AdminUpdateUserData } from '@/lib/api';
import type { User, GlobalRole, UserAdminDetails } from '@/types';

interface UserListItem {
  id: string;
  email: string;
  displayName: string;
  publicName: string | null;
  avatarUrl: string | null;
  role: GlobalRole;
  isActive: boolean;
}

export function useUsers() {
  return useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const response = await usersApi.getAll();
      return response.data as UserListItem[];
    },
  });
}

export function useUser(id: string | undefined) {
  return useQuery({
    queryKey: ['users', id],
    queryFn: async () => {
      if (!id) throw new Error('User ID required');
      const response = await usersApi.getById(id);
      return response.data as User;
    },
    enabled: !!id,
  });
}

export function useUpdateUserRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, role }: { id: string; role: GlobalRole }) => {
      const response = await usersApi.updateRole(id, role);
      return response.data as User;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function useDeactivateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await usersApi.deactivate(id);
      return response.data as User;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function useActivateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await usersApi.activate(id);
      return response.data as User;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

// =============================================
// ADMIN USER MANAGEMENT HOOKS
// =============================================

export function useUserAdminDetails(id: string | undefined) {
  return useQuery({
    queryKey: ['users', id, 'admin'],
    queryFn: async () => {
      if (!id) throw new Error('User ID required');
      const response = await usersApi.getAdminDetails(id);
      return response.data as UserAdminDetails;
    },
    enabled: !!id,
  });
}

export function useAdminUpdateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: AdminUpdateUserData }) => {
      const response = await usersApi.adminUpdate(id, data);
      return response.data as UserAdminDetails;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['users', variables.id, 'admin'] });
    },
  });
}

export function useAdminSetPassword() {
  return useMutation({
    mutationFn: async ({ id, password }: { id: string; password: string }) => {
      const response = await usersApi.adminSetPassword(id, password);
      return response.data as { success: boolean };
    },
  });
}

export function useAdminSendPasswordReset() {
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await usersApi.adminSendPasswordReset(id);
      return response.data as { success: boolean; message: string };
    },
  });
}

export function useAdminUpdateUserTeams() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, teamIds }: { id: string; teamIds: string[] }) => {
      const response = await usersApi.adminUpdateTeams(id, teamIds);
      return response.data as UserAdminDetails;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['users', variables.id, 'admin'] });
      queryClient.invalidateQueries({ queryKey: ['teams'] });
    },
  });
}

export function useAdminUpdateUserProjects() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, projectIds }: { id: string; projectIds: string[] }) => {
      const response = await usersApi.adminUpdateProjects(id, projectIds);
      return response.data as UserAdminDetails;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['users', variables.id, 'admin'] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}
